import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  intelligenceAPI,
  SupplierQualification,
  QualificationTemplate,
  QualificationResponse,
} from '../../lib/api';
import { toast } from 'sonner';
import { useIsCustomer, useIsSupplier } from '../../hooks/usePortalMode';
import { ResponseMap, DialogState, DialogFormState } from './types';
import { EDITABLE_STATUSES } from './constants';
import { getApiErrorMessage } from '../../utils/errorUtils';

export function useQualificationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCustomer = useIsCustomer();
  const isSupplier = useIsSupplier();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qualification, setQualification] = useState<SupplierQualification | null>(null);
  const [template, setTemplate] = useState<QualificationTemplate | null>(null);
  const [responses, setResponses] = useState<ResponseMap>({});
  const [uploadingFiles, setUploadingFiles] = useState<Record<number, boolean>>({});
  const [activeTab, setActiveTab] = useState('overview');

  // Dialog states
  const [dialogs, setDialogs] = useState<DialogState>({
    approve: false,
    reject: false,
    info: false,
  });
  const [dialogForm, setDialogForm] = useState<DialogFormState>({
    approveComments: '',
    rejectReason: '',
    infoRequest: '',
  });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (id) {
      fetchQualification();
    }
  }, [id, isCustomer]);

  const fetchQualification = async () => {
    setLoading(true);
    try {
      const qualRes = await intelligenceAPI.getQualification(parseInt(id!));
      setQualification(qualRes.data);

      if (qualRes.data.template) {
        const templateRes = await intelligenceAPI.getTemplate(qualRes.data.template);
        setTemplate(templateRes.data);
      }

      // The default DRF page size is 20 — qualifications with more answered
      // questions than that (common on full compliance templates) would only
      // get the first 20 responses mapped and the rest of the page rendered
      // as 'no answer'. Walk the ?page= cursor until all pages are mapped.
      const responseMap: ResponseMap = {};
      for (let page = 1; page <= 50; page++) {
        try {
          const resp = await intelligenceAPI.getResponses({ qualification: id, page, page_size: 100 });
          (resp.data.results || []).forEach((r: QualificationResponse) => {
            responseMap[r.question] = r;
          });
          if (!(resp.data as { next?: string | null }).next) break;
        } catch {
          break;
        }
      }
      setResponses(responseMap);
    } catch (error) {
      console.error('Failed to fetch qualification:', error);
      toast.error('Failed to load qualification');
    } finally {
      setLoading(false);
    }
  };

  const handleResponseChange = (questionId: number, value: string | number | boolean | string[], questionType: string) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        question: questionId,
        ...(questionType === 'text' || questionType === 'textarea' ? { text_value: String(value) } : {}),
        ...(questionType === 'number' ? { number_value: Number(value) } : {}),
        ...(questionType === 'date' ? { date_value: String(value) } : {}),
        ...(questionType === 'yes_no' ? { boolean_value: Boolean(value) } : {}),
        ...(questionType === 'single_choice' ? { choice_value: [String(value)] } : {}),
        ...(questionType === 'multiple_choice' ? { choice_value: value as string[] } : {}),
        ...(questionType === 'rating' ? { number_value: Number(value) } : {}),
      },
    }));
  };

  const handleFileUpload = async (questionId: number, file: File) => {
    if (!qualification) return;
    setUploadingFiles((prev) => ({ ...prev, [questionId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('question_id', String(questionId));
      formData.append('qualification_id', String(qualification.id));

      const res = await intelligenceAPI.uploadResponseFile(formData);
      const { file_path, file_name } = res.data;

      // The server already persisted the file against the response; mirror it
      // into local state so the answer shows immediately and is included in the
      // next Save Draft / Submit payload.
      setResponses((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          question: questionId,
          file_value: file_path,
          file_name,
        },
      }));
      toast.success('File uploaded successfully');
    } catch (error: unknown) {
      console.error('Failed to upload file:', error);
      toast.error(getApiErrorMessage(error, 'Failed to upload file'));
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  const handleFileDownload = async (questionId: number) => {
    if (!qualification) return;
    try {
      const res = await intelligenceAPI.downloadResponseFile(qualification.id, questionId);
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = responses[questionId]?.file_name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error: unknown) {
      console.error('Failed to download file:', error);
      toast.error(getApiErrorMessage(error, 'Failed to download file'));
    }
  };

  const handleFileRemove = (questionId: number) => {
    // Clear locally; the empty file_value persists on the next Save Draft.
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        question: questionId,
        file_value: '',
        file_name: '',
      },
    }));
  };

  const handleSaveResponses = async () => {
    if (!qualification) return;
    setSaving(true);
    try {
      const responsesToSave = Object.entries(responses).map(([questionId, resp]) => ({
        ...resp,
        question: parseInt(questionId),
      }));

      await intelligenceAPI.bulkUpdateResponses(qualification.id, responsesToSave);
      toast.success('Responses saved successfully');
      fetchQualification();
    } catch (error: unknown) {
      console.error('Failed to save responses:', error);
      toast.error(getApiErrorMessage(error, 'Failed to save responses'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (responseNote?: string) => {
    if (!qualification) return;
    setActionLoading(true);
    try {
      // Save responses first
      const responsesToSave = Object.entries(responses).map(([questionId, resp]) => ({
        ...resp,
        question: parseInt(questionId),
      }));
      await intelligenceAPI.bulkUpdateResponses(qualification.id, responsesToSave);

      // Then submit
      await intelligenceAPI.submitQualification(qualification.id, responseNote);
      toast.success('Qualification submitted for review');
      fetchQualification();
    } catch (error: unknown) {
      console.error('Failed to submit:', error);
      toast.error(getApiErrorMessage(error, 'Failed to submit qualification'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!qualification) return;
    setActionLoading(true);
    try {
      await intelligenceAPI.approveQualification(qualification.id, dialogForm.approveComments);
      toast.success('Qualification approved');
      setDialogs(prev => ({ ...prev, approve: false }));
      setDialogForm(prev => ({ ...prev, approveComments: '' }));
      fetchQualification();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to approve'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!qualification || !dialogForm.rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await intelligenceAPI.rejectQualification(qualification.id, dialogForm.rejectReason);
      toast.success('Qualification rejected');
      setDialogs(prev => ({ ...prev, reject: false }));
      setDialogForm(prev => ({ ...prev, rejectReason: '' }));
      fetchQualification();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to reject'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestInfo = async () => {
    if (!qualification || !dialogForm.infoRequest.trim()) return;
    setActionLoading(true);
    try {
      await intelligenceAPI.requestInfo(qualification.id, dialogForm.infoRequest);
      toast.success('Information request sent');
      setDialogs(prev => ({ ...prev, info: false }));
      setDialogForm(prev => ({ ...prev, infoRequest: '' }));
      fetchQualification();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to send request'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCalculateScore = async () => {
    if (!qualification) return;
    setActionLoading(true);
    try {
      const result = await intelligenceAPI.calculateScore(qualification.id);
      toast.success(`Score calculated: ${result.data.score_percentage}%`);
      fetchQualification();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to calculate score'));
    } finally {
      setActionLoading(false);
    }
  };

  // Permission checks
  const canApprove = isCustomer && (qualification?.status === 'submitted' || qualification?.status === 'under_review');
  const canReject = isCustomer && qualification?.status !== 'approved' && qualification?.status !== 'rejected';
  const canRequestInfo = isCustomer && qualification?.status !== 'approved' && qualification?.status !== 'rejected';
  const canSubmit = isSupplier && EDITABLE_STATUSES.includes(qualification?.status || '');
  const canCalculateScore = isCustomer && qualification?.status === 'submitted';

  return {
    // Route params
    id,
    navigate,

    // User info
    isCustomer,
    isSupplier,

    // Loading states
    loading,
    saving,
    actionLoading,

    // Data
    qualification,
    fetchQualification,
    template,
    responses,
    uploadingFiles,
    activeTab,
    setActiveTab,

    // Dialog state
    dialogs,
    setDialogs,
    dialogForm,
    setDialogForm,

    // Permissions
    canApprove,
    canReject,
    canRequestInfo,
    canSubmit,
    canCalculateScore,

    // Actions
    handleResponseChange,
    handleFileUpload,
    handleFileDownload,
    handleFileRemove,
    handleSaveResponses,
    handleSubmit,
    handleApprove,
    handleReject,
    handleRequestInfo,
    handleCalculateScore,
  };
}
